/**
 * Allys Stats Section
 * 
 * Displays key statistics/numbers to build trust and credibility
 * Inspired by Apple and Kinde marketing pages
 */

'use client';

import { motion } from 'framer-motion';
import type { StatsSectionProps } from '@routeroyal/theme-core';

/**
 * Allys Stats Section Component
 * 
 * Shows impressive numbers to build credibility with premium design
 */
export function AllysStatsSection({ stats }: StatsSectionProps) {
  return (
    <section className="py-20 lg:py-28 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.015] bg-[radial-gradient(circle_at_50%_50%,var(--color-primary)_1px,transparent_1px)] bg-[length:40px_40px]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-center group"
            >
              {/* Value - Large, bold typography */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 + 0.2 }}
                className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-3 tracking-tight"
              >
                {stat.value}
              </motion.div>

              {/* Label */}
              <p className="text-gray-600 text-sm md:text-base font-medium leading-relaxed">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

